import { format } from 'date-fns';
import moment from 'moment';
import { Moment } from 'moment';
import {
  LONG_DATE_MASK,
  PICKER_DATE_FULL_TIME_NUMBER,
  PICKER_DATE_MASK,
  PICKER_DATE_MASK_FULL_TIME,
  PICKER_SHORT_DATE_MASK,
  PICKER_SHORT_YEAR_DATE_MASK,
  PICKER_TIME_MASK,
  SERVER_DATE_MASK,
} from 'shared/constants';
import { TUserRvdInfo } from 'shared/types/rvd.types';
import { TUserOvertimeInfo } from 'shared/types/overtime.types';
import dayjs, { Dayjs } from 'dayjs';
import { MINUTE_STEP } from 'shared/constants/date';
import { Calendar, Locale } from 'shared/lib/react-date-object';
import { DateObject } from '../lib/react-date-object/date-object';
import { addZeroToStart } from './string';
import { rangeHoursDisabledTimePolls, rangeMinutesDisabledTimePolls } from './utils';

export const getCurrentYear = () => format(new Date(), 'yyyy');

/* Start convert date function */
export type TСonvertibleDate = Date | string | number | Dayjs;
interface IConvertDateSettings {
  format:
    | typeof SERVER_DATE_MASK
    | typeof PICKER_DATE_MASK
    | typeof PICKER_DATE_MASK_FULL_TIME
    | typeof PICKER_SHORT_YEAR_DATE_MASK
    | typeof PICKER_TIME_MASK
    | typeof LONG_DATE_MASK
    | typeof PICKER_DATE_FULL_TIME_NUMBER
    | typeof PICKER_SHORT_DATE_MASK
    | string;
  noData: string;
}
const defaultConvertDateSettings: IConvertDateSettings = {
  format: PICKER_DATE_MASK,
  noData: '',
};

/**
 * Функция, конвертирующая даты в необходимый формат
 * @param {TСonvertibleDate} date - дата
 * @param {Partial<IConvertDateSettings> | undefined} settings - настройки конвертации (опционально)
 * @returns {string} дата в новом формате
 * @example
 * convertDate(new Date());
 * convertDate(new Date(), { format: 'DD.MM.YYYY', noData: '' });
 */
export const convertDate = (
  date?: TСonvertibleDate,
  {
    format = defaultConvertDateSettings.format,
    noData = defaultConvertDateSettings.noData,
  }: Partial<IConvertDateSettings> = defaultConvertDateSettings,
): string => {
  return date ? dayjs(date).format(format) : noData;
};

export const сonvertDateWithNullCheck = (date: string | null): string | null => {
  return date ? convertDate(date) : null;
};

export const convertDateForServer = (date: TСonvertibleDate, options: Partial<IConvertDateSettings> = {}): string => {
  return convertDate(date, {
    format: SERVER_DATE_MASK,
    ...options,
  });
};

export const currentTimeToBack = (): string => {
  return moment().format(SERVER_DATE_MASK);
};
/* End convert date function */

export const calcEpoch = () => {
  const currentDate = new Date();
  let julianDayDiff: number;
  // if (currentDate.getDate() < 14 && currentDate.getMonth() <= 3) {
  //   julianDayDiff = 594;
  // } else {
  //   julianDayDiff = 593;
  // }
  const julianDay = new DateObject().toJulianDay();
  const days = new DateObject().subtract(593 * 365 + 249, 'days').toDays();
  return julianDay - days;
};

export const bangla: Calendar = {
  name: 'bangla',
  startYear: 1,
  yearLength: 365,
  epoch: calcEpoch(),
  century: 14,
  weekStartDayIndex: 1,
  getMonthLengths(isLeap) {
    return [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, isLeap ? 30 : 29, 30];
  },
  isLeap(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  },
  getLeaps(currentYear) {
    if (currentYear === 0) return [];

    let year = currentYear > 0 ? 1 : -1;

    const leaps: number[] = [],
      condition = () => (currentYear > 0 ? year <= currentYear : currentYear <= year),
      increase = () => (currentYear > 0 ? year++ : year--);

    while (condition()) {
      if (this.isLeap(year)) leaps.push(year);

      increase();
    }

    return leaps;
  },
  getDayOfYear({ year, month, day }) {
    const monthLengths = this.getMonthLengths(this.isLeap(year));

    for (let i = 0; i < month.index; i++) {
      day += monthLengths[i];
    }

    return day;
  },
  getAllDays(date) {
    const { year } = date;

    return this.yearLength * (year - 1) + (this.leapsLength ? this.leapsLength(year) : 0) + this.getDayOfYear(date);
  },
  leapsLength(year) {
    return (((year - 1) / 4) | 0) + (-((year - 1) / 100) | 0) + (((year - 1) / 400) | 0);
  },
  guessYear(days, currentYear) {
    const year = ~~(days / 365.24);

    return year + (currentYear > 0 ? 1 : -1);
  },
};

export const locale: Locale = {
  name: 'bd_locale',
  months: [
    ['বৈশাখ', 'বৈ'], //Boishakh
    ['জ্যৈষ্ঠ', 'জ্য'], //Joishtha
    ['আষাঢ় ', 'আষ'], //Ashar
    ['শ্রাবণ', 'শ্র'], //Srabon
    ['ভাদ্র', 'ভাদ'], //Bhadra
    ['আশ্বিন', 'আশ্ব'], //Ashwin
    ['কার্তিক', 'কার্'], //Kartik
    ['অগ্রহায়ণ', 'আগ'], //Ogrohayon
    ['পৌষ', 'পৌ'], //Poush
    ['মাঘ', 'মাঘ'], //Magh
    ['ফাল্গুন', 'ফাল্'], //Falgun
    ['চৈত্র', 'চৈ'], //Chaitra
  ] as unknown as [string[]],
  weekDays: [
    ['শনিবার', 'শনি'], // Sut
    ['রবিবার', 'রবি'], // Sun
    ['সোমবার', 'সোম'], // Mon
    ['মঙ্গলবার', 'মঙ্গল'], // Tue
    ['বুধবার', 'বুধ'], // dance dance dance with my hands
    ['বৃহষ্পতিবার', 'বৃহঃ'], // Thu
    ['শুক্রবার', 'শুক্র'], // Fry
  ] as unknown as [string[]],
  digits: ['๐', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'],
  meridiems: [
    ['AM', 'পূর্বাহ্ন'],
    ['PM', 'অপরাহ্ন'],
  ] as unknown as [string[]],
};

export const getPrettiedHoursAndMinutes = (dates: [Dayjs, Dayjs]): string => {
  const diff = dates[1].diff(dates[0]) + 1000;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(diff / (1000 * 60)) - hours * 60;
  const prettiedDate = `${addZeroToStart(hours)}:${addZeroToStart(minutes)}`;
  return prettiedDate;
};

export const calcWorkDayDuration = (elem: TUserRvdInfo): string => {
  const workDayDuration = getPrettiedHoursAndMinutes([
    dayjs(elem.startTime, PICKER_TIME_MASK),
    dayjs(elem.endTime, PICKER_TIME_MASK),
  ]);
  if (elem.endBreakTime && elem.startBreakTime) {
    const breakDuration = getPrettiedHoursAndMinutes([
      dayjs(elem.startBreakTime, PICKER_TIME_MASK),
      dayjs(elem.endBreakTime, PICKER_TIME_MASK),
    ]);
    return getPrettiedHoursAndMinutes([
      dayjs(breakDuration, PICKER_TIME_MASK),
      dayjs(workDayDuration, PICKER_TIME_MASK),
    ]);
  }
  return workDayDuration;
};

export const calcWorkDayDurationOvertime = (elem: TUserOvertimeInfo): string => {
  const workDayDuration = getPrettiedHoursAndMinutes([
    dayjs(elem.startTime, PICKER_TIME_MASK),
    dayjs(elem.endTime, PICKER_TIME_MASK),
  ]);
  return workDayDuration;
};

export const isStartDateIsAfterToday = (startDate: string | undefined) => {
  const now = dayjs();
  const day = now.date();
  const month = now.month() + 1;
  const year = now.year();

  const today = dayjs(`${day < 10 ? '0' + day : day}.${month < 10 ? '0' + month : month}.${year}`, PICKER_DATE_MASK);
  const start = dayjs(startDate, PICKER_DATE_MASK);

  return start.isAfter(today);
};

export function disabledStartTimePoll(startDate: string | undefined) {
  const currentDate = moment(Date.now());
  let currentHour = currentDate.hour();
  const currentMinute = currentDate.minute();
  const maxPossibleMinute = 60 - MINUTE_STEP;

  if (currentMinute >= maxPossibleMinute) {
    currentHour = currentDate.add(1, 'h').hour();
  }

  const needToCheck = isStartDateIsAfterToday(startDate);
  if (needToCheck) {
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  }
  return {
    disabledHours: () => rangeHoursDisabledTimePolls(currentHour),
    disabledMinutes: (selectedHour: number) => rangeMinutesDisabledTimePolls(selectedHour, currentHour, currentMinute),
  };
}

export function disabledDateTime(data: [Moment, Moment] | undefined) {
  if (!data) {
    return {
      disabledHours: () => [],
    };
  }
  const startHours = data[0].hour();
  const endHours = data[1].hour();

  function range(start, end) {
    const result: number[] = [];
    for (let i = 0; i < start; i++) {
      result.push(i);
    }
    for (let i = end; i < 24; i++) {
      result.push(i);
    }
    return result;
  }

  return {
    disabledHours: () => range(+startHours, +endHours),
  };
}

export function disabledTime(dateStart?: string, dateEnd?: string, time?: string) {
  const diff = moment(dateEnd, PICKER_DATE_MASK).diff(moment(dateStart, PICKER_DATE_MASK));

  if (diff > 0) {
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  }
  const edgeTime = moment(time, PICKER_TIME_MASK);

  const hours = edgeTime.hour();
  const minutes = edgeTime.minute();

  return {
    disabledHours: () => rangeHoursDisabledTimePolls(hours),
    disabledMinutes: (selectedHour: number) => rangeMinutesDisabledTimePolls(selectedHour, hours, minutes),
  };
}

export const disableAllPastDays = ({ date }) => {
  const dateDay = date.toDate();
  const today = new Date();
  const diff = moment(today).diff(date.toDate());
  if (diff > 0 && !(dateDay.toDateString() === today.toDateString()))
    return {
      disabled: true,
      style: { color: '#ccc' },
    };
};

export const setDateFromString = (data: string | undefined, format = PICKER_DATE_MASK): Dayjs | undefined => {
  if (data) {
    return dayjs(data, format);
  }
};

export const changeFrontTypeDateToBack = (date: string) => {
  return moment(date, PICKER_DATE_MASK).format(SERVER_DATE_MASK);
};

export const checkDate = (date: string | undefined, time: string | undefined, auto: boolean): string | undefined => {
  if (!auto) {
    return undefined;
  }
  if (date && time) {
    return `${changeFrontTypeDateToBack(date)} ${time}`;
  }
};

export const getStartOfDayInMilliseconds = (): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Устанавливаем время на начало текущего дня
  return today.getTime(); // Возвращаем время в миллисекундах
};

export const checkStartDatePoll = (startDate?: string): boolean => {
  if (!startDate) {
    return false;
  }

  const utc = moment(startDate).subtract(3, 'hours');
  const diff = utc.diff(new Date(), 'minutes');

  if (diff > 0) {
    return true;
  }
  return false;
};

/** Возвращает ближайшее будущее время для пикера */
export const getClosestBiggerTime = () => {
  let temp = 0;
  let current = dayjs();
  let currentMinute = current.minute();

  let currentMinuteWasChanged = false;
  while (temp < 60) {
    if (currentMinute < temp) {
      currentMinute = temp;
      currentMinuteWasChanged = true;
      break;
    }

    temp += MINUTE_STEP;
  }
  if (!currentMinuteWasChanged) {
    current = current.add(1, 'h');
    currentMinute = 0;
  }
  const currentHour = current.hour();
  if (currentHour > 24) {
    return '00:00:00';
  }

  return `${currentHour < 10 ? '0' + currentHour : currentHour}:${
    currentMinute < 10 ? '0' + currentMinute : currentMinute
  }:00`;
};

/** Возвращает инфу для пикера с проверкой по минутам */
export const checkDisabledDatesWithMinuteStep = (current: Dayjs, isMinuteCheckRequired?: boolean) => {
  const afterMinuteStep = current.add(MINUTE_STEP, 'm');
  const daysDiff = afterMinuteStep.day() - current.day();
  if (isMinuteCheckRequired && daysDiff > 0) {
    return current && current < dayjs().endOf('day');
  }
  return current && current < dayjs().startOf('day');
};
